"use client";

import { useState } from "react";
import OverlayDialog from "@/components/ui/OverlayDialog";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Menu,
  X,
  LayoutDashboard,
  Home,
  Mountain,
  UtensilsCrossed,
  Star,
  FileText,
  HelpCircle,
  Landmark,
  CalendarDays,
  Mail,
  Settings,
  Activity,
  Search,
  Users,
  LogOut,
  ExternalLink,
  TrendingUp,
  Newspaper,
  Image as ImageIcon,
  LayoutTemplate,
  Map as MapIcon,
} from "lucide-react";
import { signOutAction } from "../actions";

type NavItem = { href: string; label: string; icon: typeof LayoutDashboard };
type NavSection = { heading?: string; items: NavItem[] };

const navSections: NavSection[] = [
  {
    items: [
      { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
      { href: "/admin/visitors", label: "Visitors", icon: Users },
      { href: "/admin/seo", label: "SEO Score", icon: Search },
      { href: "/admin/search-performance", label: "Search Rankings", icon: TrendingUp },
      { href: "/admin/api-health", label: "API Health", icon: Activity },
      { href: "/admin/bookings", label: "Bookings", icon: CalendarDays },
      { href: "/admin/contact-submissions", label: "Messages", icon: Mail },
      { href: "/admin/newsletter", label: "Newsletter", icon: Newspaper },
      { href: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
  {
    heading: "Content",
    items: [
      { href: "/admin/pages", label: "Pages & Copy", icon: LayoutTemplate },
      { href: "/admin/media-map", label: "Media Map", icon: MapIcon },
      { href: "/admin/media", label: "Media Library", icon: ImageIcon },
      { href: "/admin/cabins", label: "Properties", icon: Home },
      { href: "/admin/activities", label: "Activities", icon: Mountain },
      { href: "/admin/dining", label: "Dining", icon: UtensilsCrossed },
      { href: "/admin/reviews", label: "Reviews", icon: Star },
      { href: "/admin/blog", label: "Blog", icon: FileText },
      { href: "/admin/faq", label: "FAQ", icon: HelpCircle },
      { href: "/admin/culture", label: "Culture", icon: Landmark },
    ],
  },
];

export default function AdminSidebar({
  user,
}: {
  user: { name?: string | null; email?: string | null };
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  const navigation = (
    <div className="flex h-full min-h-0 flex-col bg-[#1A1A2E] text-white">
      <div className="px-5 py-4 border-b border-white/10">
        <Link href="/admin" className="block min-h-11 content-center text-left text-lg font-light tracking-wider">
          <span >Kettura Admin</span>
        </Link>
        <Link
          href="/"
          title="Back to website"
          className="flex items-center justify-start gap-1.5 min-h-11 text-xs text-white/40 hover:text-white/70 transition-colors"
        >
          <ExternalLink size={12} />
          <span >Back to website</span>
        </Link>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        {navSections.map((section, si) => (
          <div key={si}>
            {section.heading && (
              <p className="px-5 pt-5 pb-2 text-[10px] uppercase tracking-[0.2em] text-white/30 font-medium">
                {section.heading}
              </p>
            )}
            {section.items.map((item) => {
              const isActive =
                pathname === item.href ||
                (item.href !== "/admin" && pathname.startsWith(item.href));
              const Icon = item.icon;

              return (
                <Link
                  key={item.href}
                  href={item.href}
                  title={item.label}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  className={`flex items-center justify-start gap-3 min-h-11 px-5 py-3 text-sm transition-colors ${
                    isActive
                      ? "bg-white/10 text-white border-r-2 border-[#9B7B3A]"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon size={18} />
                  <span >{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-white/10">
        <div className="text-xs text-white/40 mb-2 truncate">
          {user.email}
        </div>
        <form action={signOutAction}>
          <button
            type="submit"
            title="Sign out"
            className="flex w-full items-center justify-start gap-2 min-h-11 py-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <LogOut size={16} />
            <span >Sign out</span>
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <>
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 bg-[#1A1A2E] px-4 text-white xl:hidden">
        <Link href="/admin" className="flex min-h-11 items-center font-sans tracking-wide">Kettura Admin</Link>
        <button type="button" onClick={() => setOpen(true)} aria-label="Open admin menu" aria-expanded={open} aria-haspopup="dialog" className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10"><Menu size={24} /></button>
      </header>
      <aside className="hidden h-full w-64 shrink-0 xl:block">{navigation}</aside>
      <OverlayDialog open={open} onClose={() => setOpen(false)} label="Admin navigation" className="inset-y-0 right-auto left-0 m-0 h-dvh w-[min(100%,20rem)] bg-[#1A1A2E] text-white">
        <div className="flex h-full flex-col">
          <div className="flex shrink-0 justify-end px-3 pt-2"><button type="button" onClick={() => setOpen(false)} aria-label="Close admin menu" className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10"><X size={24} /></button></div>
          <div className="min-h-0 flex-1">{navigation}</div>
        </div>
      </OverlayDialog>
    </>
  );
}
